/**
 * AlertsPanel — Panel deslizante de alertas proactivas.
 *
 * Slide-over desde la derecha. Consume AlertsContext.
 * Alertas ordenadas: Critical → Warning → Info.
 * Cada alerta tiene borde izquierdo coloreado, icono por tipo,
 * botón de acción y botón de dismiss (X).
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Bell, CheckCheck, AlertTriangle, Info,
  FileText, CreditCard, DollarSign, BookOpen,
  Loader2, CheckCircle2,
} from 'lucide-react';
import { useAlertsContext } from '../context/AlertsContext';
import type { QuiPusAlert } from '../services/AlertsEngine';
import { useLang } from '../context/LangContext';
import { t } from '../i18n/dictionary';
import { useTheme } from '../context/ThemeContext';
import { colors } from '../utils/theme';

// ─── Icon map by alert type ───────────────────────────────────────────────────

const TYPE_ICONS: Record<QuiPusAlert['type'], React.ElementType> = {
  sunat:      FileText,
  cxp:        CreditCard,
  cxc:        DollarSign,
  contable:   BookOpen,
  tributario: FileText,
};

// ─── Severity color tokens ───────────────────────────────────────────────────

const SEVERITY = {
  critical: {
    border: '',
    bg:     'bg-[var(--surface-raised)]',
    icon:   'text-[var(--negative)]',
    badge:  'bg-[var(--surface-raised)] text-[var(--negative)] border border-[var(--border)]',
  },
  warning: {
    border: '',
    bg:     'bg-[var(--surface-raised)]',
    icon:   'text-[var(--warning)]',
    badge:  'bg-[var(--surface-raised)] text-[var(--warning)] border border-[var(--border)]',
  },
  info: {
    border: '',
    bg:     'bg-[var(--brand-soft)]',
    icon:   'text-[var(--link)]',
    badge:  'bg-[var(--brand-soft)] text-[var(--link)] border border-[var(--brand-bd)]',
  },
} as const;

const SEVERITY_LABELS: Record<QuiPusAlert['severity'], string> = {
  critical: 'Crítico',
  warning:  'Aviso',
  info:     'Info',
};

const SEVERITY_ORDER: Record<QuiPusAlert['severity'], number> = {
  critical: 0,
  warning:  1,
  info:     2,
};

// ─── Individual Alert Card ────────────────────────────────────────────────────

function AlertCard({ alert, onDismiss }: { alert: QuiPusAlert; onDismiss: (id: string) => void }) {
  const { lang } = useLang();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);
  const s   = SEVERITY[alert.severity];
  const Icon = TYPE_ICONS[alert.type] ?? BookOpen;
  const SevIcon = alert.severity === 'critical' ? AlertTriangle
                : alert.severity === 'warning'  ? AlertTriangle
                : Info;

  return (
    <div
      className={`relative rounded-xl ${s.border} ${s.bg} p-4`}
      style={{
        borderTop:    `1px solid ${c.borderSoft}`,
        borderRight:  `1px solid ${c.borderSoft}`,
        borderBottom: `1px solid ${c.borderSoft}`,
        transition:   'opacity 0.2s',
      }}
    >
      {/* Dismiss button */}
      <button
        onClick={() => onDismiss(alert.id)}
        className={`absolute top-3 right-3 w-5 h-5 rounded flex items-center justify-center text-slate-500 hover:text-slate-300 transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
        title={t('alerts.dismiss24', lang)}
      >
        <X size={11} />
      </button>

      <div className="flex items-start gap-3 pr-5">
        {/* Type icon */}
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${s.bg}`}
          style={{ border: `1px solid ${c.border}` }}
        >
          <Icon size={13} className={s.icon} />
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          {/* Severity badge + title */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 t-3xs font-bold px-1.5 py-0.5
              rounded uppercase tracking-wide ${s.badge}`}
            >
              <SevIcon size={9} />
              {SEVERITY_LABELS[alert.severity]}
            </span>
            <span className="t-2xs font-bold leading-tight" style={{ color: c.textPrimary }}>
              {alert.title}
            </span>
          </div>

          {/* Message */}
          <p className="t-2xs text-slate-400 leading-relaxed">
            {alert.message}
          </p>

          {/* Action button */}
          {alert.action && alert.actionUrl && (
            <button
              onClick={() => navigate(alert.actionUrl!)}
              className="inline-flex items-center gap-1 t-xs font-bold text-[var(--link)]
                hover:text-[var(--link)] transition-colors mt-0.5"
            >
              {alert.action} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export default function AlertsPanel() {
  const { lang } = useLang();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);
  const { alerts, alertCount, dismissAlert, dismissAll, panelOpen, closePanel, loading } =
    useAlertsContext();

  // Close on Escape
  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [panelOpen, closePanel]);

  if (!panelOpen) return null;

  const activeAlerts = alerts
    .filter(a => !a.dismissed)
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
        onClick={closePanel}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-md z-50 shadow-2xl flex flex-col"
        style={{
          background: c.modal,
          borderLeft: `1px solid ${c.border}`,
          animation: 'slideInRight 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${c.borderSoft}` }}
        >
          <div className="flex items-center gap-2.5">
            <Bell size={16} className="text-[var(--link)]" />
            <span className="text-sm font-bold" style={{ color: c.textPrimary }}>Alertas</span>
            {alertCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5
                rounded-full bg-red-500 text-white t-3xs font-bold px-1.5"
              >
                {alertCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {activeAlerts.length > 0 && (
              <button
                onClick={dismissAll}
                className="flex items-center gap-1 t-xs font-semibold text-slate-400
                  hover:text-slate-200 transition-colors"
                title={t('alerts.dismissAll24', lang)}
              >
                <CheckCheck size={11} />
                {t('alerts.markAllRead', lang)}
              </button>
            )}
            <button
              onClick={closePanel}
              className={`w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 transition-colors ${isDark ? 'hover:text-white hover:bg-white/10' : 'hover:text-slate-900 hover:bg-slate-100'}`}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 size={20} className="text-slate-500 animate-spin" />
            </div>
          ) : activeAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <CheckCircle2 size={36} className="text-[var(--positive)] opacity-80" />
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: c.textPrimary }}>Todo al día</p>
                <p className="text-xs text-slate-500 mt-0.5">Sin alertas pendientes</p>
              </div>
            </div>
          ) : (
            activeAlerts.map(alert => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onDismiss={dismissAlert}
              />
            ))
          )}
        </div>

        {/* Footer — dismissed count */}
        {alerts.filter(a => a.dismissed).length > 0 && (
          <div className="px-5 py-2.5 flex-shrink-0" style={{ borderTop: `1px solid ${c.borderSoft}` }}>
            <p className="t-xs text-slate-600">
              {alerts.filter(a => a.dismissed).length} alerta{alerts.filter(a => a.dismissed).length !== 1 ? 's' : ''} descartada{alerts.filter(a => a.dismissed).length !== 1 ? 's' : ''} (expiran en 24 h)
            </p>
          </div>
        )}
      </div>

      {/* CSS animation */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
